import React, { useEffect, useState } from "react";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import GridProductCard from "../../components/gridProductCard";
import FullscreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import { appApiHandler } from "../../api/appApiHandler";
import "./CategoryProduct.css";
import { getUserType } from "../../constants/asyncStoreManager";
import { IoIosArrowDown } from "react-icons/io";
import { IoFilter } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { Range } from "react-range";
import Breadcrumb from "../../components/breadcrumb";
import { colors } from "../../constants/colors";
import EmptyState from "../../components/EmptyState";
import { FaShoppingBag } from "react-icons/fa";

const CategoryProduct = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlCategoryId = urlParams.get("category_id");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fabrics, setFabrics] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  // Filters state
  const [selectedSortOption, setSelectedSortOption] = useState("newest");
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [categoryObj, setCategoryObj] = useState(null);

  // UI state for accordion and temp selections before Apply
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isFabricsOpen, setIsFabricsOpen] = useState(false);
  const [isBrandsOpen, setIsBrandsOpen] = useState(false);
  const [isSizesOpen, setIsSizesOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [tempFabrics, setTempFabrics] = useState([]);
  const [tempBrands, setTempBrands] = useState([]);
  const [tempSizes, setTempSizes] = useState([]);

  // Slider state
  const [priceRange, setPriceRange] = useState([0, 50000]);
  
  // Grid state
  const [gridColumns, setGridColumns] = useState(4);
  const [effectiveGridColumns, setEffectiveGridColumns] = useState(4);

  const getFilterParams = async () => {
    const response = await appApiHandler.getProductFilterParams();
    if (response?.status === 200) {
      if (response?.fabrics) {
        setFabrics(response?.fabrics);
      }
      if (response?.brands) {
        setBrands(response?.brands);
      }
      if (response?.sizes) {
        setSizes(response?.sizes);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    getProducts().then(() => {
      setLoading(false);
    });
  }, [
    selectedSortOption,
    selectedFabrics,
    selectedBrands,
    selectedSizes,
    minPrice,
    maxPrice,
  ]);

  const getProducts = async () => {
    const userType = (await getUserType()) || "retailer";
    const categoryId = urlCategoryId;
    const reqData = {
      user_type: userType,
      category_id: categoryId,
      fabrics: selectedFabrics,
      brands: selectedBrands,
      sizes: selectedSizes,
      min_price: minPrice,
      max_price: maxPrice,
      sort_type: selectedSortOption,
    };
    const response = await appApiHandler.filterProducts(reqData);
    if (response?.status === 200) {
      setCategoryObj(response?.category || null);
      setProducts(response?.data || []);
    }
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    setLoading(true);
    Promise.all([getProducts(), getFilterParams()]).finally(() => {
      setLoading(false);
    });
  }, [urlCategoryId]);

  // ----- Handlers for filters -----
  const toggleFromArray = (array, setter, value) => {
    const exists = array.includes(value);
    const next = exists ? array.filter((v) => v !== value) : [...array, value];
    setter(next);
  };

  const handleApplyAll = () => {
    setMinPrice(priceRange[0]);
    setMaxPrice(priceRange[1]);
    setSelectedFabrics(tempFabrics);
    setSelectedBrands(tempBrands);
    setSelectedSizes(tempSizes);
    setShowFilters(false);
  };

  const handleClearAll = () => {
    setTempFabrics([]);
    setTempBrands([]);
    setTempSizes([]);

    setMinPrice("");
    setMaxPrice("");
    setPriceRange([0, 50000]);
    setSelectedFabrics([]);
    setSelectedBrands([]);
    setSelectedSizes([]);
    setShowFilters(false);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSelectedSortOption(value);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  useEffect(() => {
    setTempFabrics(selectedFabrics);
    setTempBrands(selectedBrands);
    setTempSizes(selectedSizes);

    // Sync slider with current price values
    if (minPrice !== "" && maxPrice !== "") {
      setPriceRange([minPrice, maxPrice]);
    } else {
      setPriceRange([0, 50000]);
    }
  }, [minPrice, maxPrice, selectedFabrics, selectedBrands, selectedSizes]);

  // Update effective grid columns when gridColumns or window size changes
  useEffect(() => {
    const updateEffectiveColumns = () => {
      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        // On small devices, limit to max 3 columns and default to 2
        // If user selects 4, 5, or 6 columns, default to 2 columns
        if (gridColumns >= 4) {
          setEffectiveGridColumns(2);
        } else {
          setEffectiveGridColumns(Math.min(gridColumns, 3));
        }
      } else {
        setEffectiveGridColumns(gridColumns);
      }
    };

    updateEffectiveColumns();
    
    const handleResize = () => {
      updateEffectiveColumns();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [gridColumns]);

  return (
    <div className="categoryProductPage">
      <HomeHeader />

      <div className="categoryHeader">
        <div className="categoryHeader__left">
          <h1 className="categoryHeader__title">{categoryObj?.name}</h1>
        </div>
        <div className="categoryHeader__right">
          <button
            className={`filterButton ${showFilters ? "active" : ""}`}
            onClick={toggleFilters}
          >
            <IoFilter size={18} />
            <span>Filter</span>
          </button>
          <div className="sortContainer">
            <select
              id="sortBy"
              className="sortSelect"
              value={selectedSortOption}
              onChange={handleSortChange}
            >
              <option value="newest">Newest First</option>
              <option value="price_low_to_high">Price Low to High</option>
              <option value="price_high_to_low">Price High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <Breadcrumb
        items={[
          {
            label: "HOME",
            path: "/",
          },
          {
            label: categoryObj?.name?.toUpperCase(),
            isActive: true,
          },
        ]}
      />
      <div className="page-content">
        <main className="categoryProductPage__main">
          {/* Filters - Vertical Accordion */}
          <div
            className={`filtersBackdrop ${showFilters ? "show" : ""}`}
            onClick={() => setShowFilters(false)}
          />

          {/* Filters Drawer */}
          <section className={`categoryFilters ${showFilters ? "show" : ""}`}>
            <div className="filtersAccordion">
              {/* Drawer Header */}
              <div className="filtersHeader">
                <h3 className="filtersHeader__title">FILTER</h3>
                <button
                  className="filtersHeader__close"
                  onClick={() => setShowFilters(false)}
                >
                  <IoClose size={20} />
                </button>
              </div>

              {/* Filters Content */}
              <div className="filtersContent">
                {/* Price */}
                <div className="accordionSection">
                  <button
                    className="accordionHeader"
                    onClick={() => setIsPriceOpen((v) => !v)}
                  >
                    <span>Price</span>
                    <IoIosArrowDown
                      size={18}
                      className={isPriceOpen ? "is-open" : ""}
                    />
                  </button>
                  {isPriceOpen && (
                    <div className="accordionBody">
                      <div className="filtersBar__priceInputs filtersAccordion__price">
                        <div className="price-slider-container">
                          <Range
                            step={1}
                            min={0}
                            max={50000}
                            values={priceRange}
                            onChange={(values) => setPriceRange(values)}
                            renderTrack={({ props, children }) => (
                              <div
                                {...props}
                                className="price-slider-track"
                                style={{
                                  ...props.style,
                                  height: "6px",
                                  width: "100%",
                                  backgroundColor: colors.lightGray2,
                                  borderRadius: "3px",
                                }}
                              >
                                <div
                                  className="price-slider-range"
                                  style={{
                                    position: "absolute",
                                    height: "100%",
                                    backgroundColor: colors.secondary,
                                    borderRadius: "3px",
                                    left: `${(priceRange[0] / 50000) * 100}%`,
                                    width: `${
                                      ((priceRange[1] - priceRange[0]) /
                                        50000) *
                                      100
                                    }%`,
                                    top: "0",
                                    bottom: "0",
                                  }}
                                />
                                {children}
                              </div>
                            )}
                            renderThumb={({ props }) => (
                              <div
                                {...props}
                                className="price-slider-thumb"
                                style={{
                                  ...props.style,
                                  height: "20px",
                                  width: "20px",
                                  backgroundColor: colors.primary,
                                  borderRadius: "50%",
                                  border: "2px solid var(--color-primary)",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                }}
                              />
                            )}
                          />
                          <span className="price-range-text">
                            Rs. {priceRange[0].toFixed(2)} — Rs.{" "}
                            {priceRange[1].toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fabrics */}
                {fabrics?.length > 0 && (
                  <div className="accordionSection">
                    <button
                      className="accordionHeader"
                      onClick={() => setIsFabricsOpen((v) => !v)}
                    >
                      <span>Fabrics</span>
                      <IoIosArrowDown
                        size={18}
                        className={isFabricsOpen ? "is-open" : ""}
                      />
                    </button>
                    {isFabricsOpen && (
                      <div className="accordionBody">
                        <div className="filtersBar__checkboxGrid">
                          {fabrics?.map((f) => (
                            <label key={f} className="filtersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempFabrics.includes(f)}
                                onChange={() =>
                                  toggleFromArray(
                                    tempFabrics,
                                    setTempFabrics,
                                    f
                                  )
                                }
                              />
                              <span>{f}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Brands */}
                {brands?.length > 0 && (
                  <div className="accordionSection">
                    <button
                      className="accordionHeader"
                      onClick={() => setIsBrandsOpen((v) => !v)}
                    >
                      <span>Brands</span>
                      <IoIosArrowDown
                        size={18}
                        className={isBrandsOpen ? "is-open" : ""}
                      />
                    </button>
                    {isBrandsOpen && (
                      <div className="accordionBody">
                        <div className="filtersBar__checkboxGrid">
                          {brands?.map((b) => (
                            <label key={b} className="filtersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempBrands.includes(b)}
                                onChange={() =>
                                  toggleFromArray(tempBrands, setTempBrands, b)
                                }
                              />
                              <span>{b}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sizes */}
                {sizes?.length > 0 && (
                  <div className="accordionSection">
                    <button
                      className="accordionHeader"
                      onClick={() => setIsSizesOpen((v) => !v)}
                    >
                      <span>Size</span>
                      <IoIosArrowDown
                        size={18}
                        className={isSizesOpen ? "is-open" : ""}
                      />
                    </button>
                    {isSizesOpen && (
                      <div className="accordionBody">
                        <div className="filtersBar__checkboxGrid">
                          {sizes?.map((s) => (
                            <label key={s} className="filtersBar__checkboxItem">
                              <input
                                type="checkbox"
                                checked={tempSizes.includes(s)}
                                onChange={() =>
                                  toggleFromArray(tempSizes, setTempSizes, s)
                                }
                              />
                              <span>{s}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="filtersActions">
                <button className="btnSecondary" onClick={handleClearAll}>
                  Clear
                </button>
                <button className="btnPrimary" onClick={handleApplyAll}>
                  Apply
                </button>
              </div>
            </div>
          </section>

          <section className="categoryProductPage__results">
            {/* Grid Selector */}
            <div className="categoryProductPage__gridSelector">
              <div className="categoryProductPage__gridOptions">
                {[1, 2, 3, 4, 5, 6].map((cols) => (
                  <button
                    key={cols}
                    className={`categoryProductPage__gridOption ${effectiveGridColumns === cols ? 'active' : ''}`}
                    onClick={() => setGridColumns(cols)}
                    title={`${cols} column${cols > 1 ? 's' : ''}`}
                  >
                    <div className={`grid-icon grid-icon--${cols}`}>
                      {Array.from({ length: cols }).map((_, i) => (
                        <div key={i} className="grid-icon-item" />
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {products?.length > 0 ? (
              <div className={`categoryProductPage__productGrid categoryProductPage__productGrid--${effectiveGridColumns}`}>
                {products?.map((item) => (
                  <GridProductCard key={item?._id} item={item} gridColumns={gridColumns} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={FaShoppingBag}
                title="No products found in this category."
                iconSize={40}
              />
            )}
          </section>
        </main>
      </div>
      <CommonFooter />
      {loading && <FullscreenLoader text="Loading..." />}
    </div>
  );
};

export default CategoryProduct;
